import React from "react";
import { etaLabel, type Eta } from "@veyso/core";
import { useEta } from "../lib/useEta.js";

/**
 * One job's progress, the same everywhere: what it is doing, how far, how long
 * is left, and — for work on our servers — that nobody has to wait for it.
 */
export const ProgressLine: React.FC<{
  /** The job's id where there is one, so the page and the jobs menu smooth one job as one number. */
  id: string;
  label: string;
  progress: number | null;
  eta: Eta | null | undefined;
  note?: string | null;
  /** For a note that is a warning (a browser export stops if the tab closes). */
  warn?: boolean;
}> = ({ id, label, progress, eta, note, warn }) => {
  const shown = useEta(id, eta);
  const words = etaLabel(shown);
  const percent = progress === null ? null : Math.round(Math.min(1, Math.max(0, progress)) * 100);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-medium text-ink-100">{label}</span>
        {percent !== null ? <span className="tnum text-[12px] text-ink-300">{percent}%</span> : null}
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-bay-700"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
      >
        <div
          className="h-full rounded-full bg-ember-500 transition-[width] duration-500"
          style={{ width: `${Math.max(3, percent ?? 0)}%` }}
        />
      </div>
      {words ? <span className="text-[13px] font-medium text-ink-100">{words}</span> : null}
      {note ? (
        <p className={`text-[12px] leading-relaxed ${warn ? "text-signal-warn" : "text-ink-300"}`}>{note}</p>
      ) : null}
    </div>
  );
};
